import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
} from '@angular/core';

import { Subject, takeUntil } from 'rxjs';
import { select, Store } from '@ngrx/store';

import { NotificationLayout, NotificationTheme } from '@fcom/ui-components';
import { CmsCollection, CmsTaxonomyGeneral, CmsTeaser, CmsViewType } from '@fcom/core-api';
import { getViewedNotifications } from '@fcom/common/store/selectors/notification.selectors';
import * as NotificationActions from '@fcom/common/store/actions/notification.actions';
import { AppState } from '@fcom/core';
import { FinnairThemeService, TestVariant } from '@fcom/common';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

interface DisruptionMessage {
  id: number;
  level: NotificationTheme;
  text: string;
  linkText: string;
  linkUrl: string;
  hidden: boolean;
  linkInNewTab: boolean;
  title?: string;
  horizontal?: boolean;
}

/**
 * There is 4 levels of disruptions for Finnair 1-4, we only show level 2 and up on the homepage.
 */
const disruptionLevelMap = {
  [CmsViewType.DISRUPTION_LEVEL_1]: NotificationTheme.SUCCESS,
  [CmsViewType.DISRUPTION_LEVEL_2]: NotificationTheme.INFO,
  [CmsViewType.DISRUPTION_LEVEL_3]: NotificationTheme.WARNING,
  [CmsViewType.DISRUPTION_LEVEL_4]: NotificationTheme.ERROR,
};

@Component({
  selector: 'cms-disruptions',
  templateUrl: './cms-disruptions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[class.grid-max-width]': '!isGlobalBanner() && isNewBrand()',
    '[class.grid-col-12]': '!isGlobalBanner() && isNewBrand()',
    '[class.no-row-gap]': '!isGlobalBanner() && isNewBrand()',
  },
})
export class CmsDisruptionsComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private store$ = inject<Store<AppState>>(Store);
  private themeService = inject(FinnairThemeService);

  readonly NotificationLayout = NotificationLayout;

  disruptions = input<CmsCollection>();
  showLevelOne = input<boolean>(false);
  testVariant = input<TestVariant>(TestVariant.A);
  isGlobalBanner = input<boolean>(false);
  layout = input<NotificationLayout>(NotificationLayout.BLOCK);

  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  private viewedNotifications: number[] = [];
  private destroyed$ = new Subject();

  getMappedDisruptions(): DisruptionMessage[] {
    if (!this.disruptions() || !this.disruptions().items) {
      return [];
    }

    return this.disruptions()
      .items.map((item: CmsTeaser): DisruptionMessage => {
        if (!disruptionLevelMap[item.viewTypeName]) {
          return null;
        }

        const baseItemContent = {
          id: item.contentId,
          level: disruptionLevelMap[item.viewTypeName],
          title: undefined,
          text: item.teaserTitle ? item.teaserTitle : item.teaserText,
          linkText: item.linkText ?? item.target?.teaserTitle ?? '',
          linkUrl: item.target?.url,
          hidden: this.viewedNotifications.includes(item.contentId),
          horizontal: true,
          linkInNewTab: item.target?.openInNewTab,
        };

        return this.isNewBrand()
          ? {
              ...baseItemContent,
              title: item.teaserText && !this.isGlobalBanner() ? item.teaserTitle : undefined,
              text: item.teaserText && !this.isGlobalBanner() ? item.teaserText : item.teaserTitle,
              horizontal: !item.subjectTaxonomyTags.includes(CmsTaxonomyGeneral.VERTICAL_LAYOUT),
            }
          : baseItemContent;
      })
      .filter(Boolean);
  }

  onClose(item: DisruptionMessage): void {
    this.store$.dispatch(NotificationActions.setViewedNotification({ id: item.id }));
  }

  /**
   * For links that want to open in a new tab override the default behavior and manually open in
   * new tab/window.
   */
  onLinkClick(event: Event, item: DisruptionMessage): void {
    if (item.linkInNewTab) {
      event.preventDefault();
      window.open(item.linkUrl, '_blank');
    }
  }

  ngOnInit(): void {
    this.store$
      .pipe(select(getViewedNotifications))
      .pipe(takeUntil(this.destroyed$))
      .subscribe((notificationIds) => {
        this.viewedNotifications = notificationIds;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  showGlobalBannerClose(item: DisruptionMessage): boolean {
    return item.level === NotificationTheme.SUCCESS || item.level === NotificationTheme.INFO;
  }
}
